<template>
  <v-card
    outlined
    rounded="lg"
    class="promotion-callout"
    :style="calloutStyleVars"
    :href="url"
    :target="openInNewTab ? '_blank' : undefined"
    :rel="openInNewTab ? rel : undefined"
  >
    <v-btn
      v-if="dismissible"
      icon
      x-small
      class="promotion-callout__dismiss"
      @click.stop.prevent="handleDismiss"
    >
      <v-icon size="16">mdi-close</v-icon>
    </v-btn>
    <div class="promotion-callout__content">
      <div v-if="$slots.icon" class="promotion-callout__icon" :style="iconStyle">
        <slot name="icon"></slot>
      </div>
      <div class="promotion-callout__text">
        <vs-text class="promotion-callout__title">{{ title }}</vs-text>
        <vs-text class="promotion-callout__subtitle">{{ message }}</vs-text>
        <div class="promotion-callout__learn-more">
          <vs-text class="promotion-callout__learn-more-text">
            {{ ctaText }}
          </vs-text>
          <v-icon v-if="showArrow" :size="ctaIconSize" class="promotion-callout__learn-more-icon">
            mdi-arrow-right
          </v-icon>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const emit = defineEmits<{
  (e: 'dismiss')
}>()

type CalloutColorProps = {
  backgroundColor: string
  borderColor: string
  titleColor: string
  messageColor: string
  ctaColor: string
}

type CalloutSpacingProps = {
  ctaBottom: string
  ctaRight: string
  iconSize: string
}

const props = withDefaults(
  defineProps<{
    title: string
    message: string
    url: string
    ctaText?: string
    ctaIconSize?: number
    showArrow?: boolean
    dismissible?: boolean
    openInNewTab?: boolean
    rel?: string
    colors?: Partial<CalloutColorProps>
    spacing?: Partial<CalloutSpacingProps>
  }>(),
  {
    ctaText: 'Learn more',
    ctaIconSize: 16,
    showArrow: true,
    dismissible: false,
    openInNewTab: true,
    rel: 'noopener',
    colors: () => ({}),
    spacing: () => ({}),
  },
)

const handleDismiss = () => {
  emit('dismiss')
}

const colorConfig = computed<CalloutColorProps>(() => ({
  backgroundColor: props.colors.backgroundColor ?? '#e9f8ef',
  borderColor: props.colors.borderColor ?? '#b7e4c7',
  titleColor: props.colors.titleColor ?? '#0f6c2a',
  messageColor: props.colors.messageColor ?? '#0f6c2a',
  ctaColor: props.colors.ctaColor ?? '#018839',
}))

const spacingConfig = computed<CalloutSpacingProps>(() => ({
  ctaBottom: props.spacing.ctaBottom ?? '-2px',
  ctaRight: props.spacing.ctaRight ?? '0',
  iconSize: props.spacing.iconSize ?? '40px',
}))

const calloutStyleVars = computed(() => ({
  '--promotion-callout-bg': colorConfig.value.backgroundColor,
  '--promotion-callout-border': colorConfig.value.borderColor,
  '--promotion-callout-title': colorConfig.value.titleColor,
  '--promotion-callout-message': colorConfig.value.messageColor,
  '--promotion-callout-cta': colorConfig.value.ctaColor,
  '--promotion-callout-cta-bottom': spacingConfig.value.ctaBottom,
  '--promotion-callout-cta-right': spacingConfig.value.ctaRight,
}))

const iconStyle = computed(() => ({
  width: spacingConfig.value.iconSize,
  height: spacingConfig.value.iconSize,
}))
</script>

<style scoped>
.promotion-callout {
  position: relative;
  border-color: var(--promotion-callout-border) !important;
  background-color: var(--promotion-callout-bg) !important;
  text-decoration: none;
}

.promotion-callout__dismiss {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.promotion-callout__content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
}

.promotion-callout__icon {
  flex-shrink: 0;
}

.promotion-callout__text {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-height: 72px;
}

.promotion-callout__text .text.vs-paragraph.promotion-callout__title {
  color: var(--promotion-callout-title);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.promotion-callout__text .text.vs-paragraph.promotion-callout__subtitle {
  color: var(--promotion-callout-message);
  font-size: 14px;
  line-height: 1.35;
}

.promotion-callout__learn-more {
  position: absolute;
  right: var(--promotion-callout-cta-right);
  bottom: var(--promotion-callout-cta-bottom);
  display: flex;
  align-items: center;
  gap: 0;
}

.promotion-callout__learn-more .text.vs-paragraph {
  color: var(--promotion-callout-cta);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.promotion-callout__learn-more .v-icon.promotion-callout__learn-more-icon {
  color: var(--promotion-callout-cta);
}
</style>
